import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { type FC, type ReactNode } from 'react';
import { TableRow, TableCell, IconButton, Collapse, Table, TableBody } from '@mui/material';

import { EyeCheckboxRender } from '../common/expandableTable/EyeCheckboxRender.tsx';
import type { ViewOptionsController } from './ViewOptionsController';
import type { ViewOptionsGroup } from '@/engine/app/interfaces';
import { observer } from 'mobx-react';
import { ContoursCheckboxRender } from './icon-renderers/ContoursCheckboxrender';
import { TransparencyIconRender } from './icon-renderers/TransparencyIconRender';
import { ViewOptionsTableRow } from './ViewOptionsTableRow';
import styled from 'styled-components';
import { useTheme } from '@mui/material/styles';

export const CollapsibleContent: FC<{
  item: ViewOptionsGroup;
  children: ReactNode;
  controller: ViewOptionsController;
  disabledVisibility: boolean;
  isCollapsed: boolean;
  toggleCollapse: () => void;
}> = observer(({ item, children, controller, disabledVisibility, isCollapsed, toggleCollapse }) => {
  const isOpen = !isCollapsed;
  const theme = useTheme();

  return (
    <>
      <ViewOptionsTableRow
        sx={{ backgroundColor: theme.customPalette.panels.collapsibleBoxes.header.background }}
        contentCell={
          <GroupItem>
            <CollapseIcon aria-label="expand row" size="small" onClick={toggleCollapse}>
              {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </CollapseIcon>
            {item.label}
          </GroupItem>
        }
        contourCell={
          <ContoursCheckboxRender
            onChange={(isContourVisible): void => controller.setGroupContourVisibility(item, isContourVisible)}
            isChecked={controller.isGroupContourVisible(item)}
          />
        }
        transparencyCell={
          <TransparencyIconRender
            transparency={controller.groupTransparency(item)}
            onClick={(transparency): void => controller.setGroupTransparency(item, transparency)}
          />
        }
        visibilityCell={
          <EyeCheckboxRender
            onChange={(isVisible): void => controller.setGroupVisibility(item, isVisible)}
            isChecked={controller.isGroupVisible(item)}
            isDisabled={disabledVisibility}
          />
        }
      />
      <TableRow>
        <TableCell colSpan={5} style={{ padding: 0 }}>
          <Collapse in={isOpen}>
            <Table size="small">
              <TableBody>{children}</TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});

const CollapseIcon = styled(IconButton)`
  color: ${({ theme }): string => theme.customPalette.panels.side.font};
`;

const GroupItem = styled.div`
  font-weight: 600;
`;
